<template>
  <div class="auditoria-wrapper">
    <ModalLoading :isLoading="isLoading" />

    <div class="auditoria-header">
      <h2 class="auditoria-title"><i class="bi bi-shield-check"></i> Registro de Actividad</h2>
      <p class="auditoria-sub">Historial de cambios realizados en el sistema</p>
    </div>

    <!-- Filtros -->
    <div class="filtros-bar">
      <div class="filtro-group">
        <label>Módulo</label>
        <select v-model="filtros.tabla" class="filtro-select">
          <option value="">Todos los módulos</option>
          <option v-for="t in tablasDisponibles" :key="t.valor" :value="t.valor">{{ t.etiqueta }}</option>
        </select>
      </div>
      <div class="filtro-group">
        <label>Tipo de acción</label>
        <select v-model="filtros.accion" class="filtro-select">
          <option value="">Todas las acciones</option>
          <option value="INSERT">Registro agregado</option>
          <option value="UPDATE">Registro modificado</option>
          <option value="DELETE">Registro eliminado</option>
        </select>
      </div>
      <div class="filtro-group">
        <label>Desde</label>
        <input v-model="filtros.fecha_inicio" type="date" class="filtro-input" />
      </div>
      <div class="filtro-group">
        <label>Hasta</label>
        <input v-model="filtros.fecha_fin" type="date" class="filtro-input" />
      </div>
      <div class="filtro-group">
        <label>Mostrar</label>
        <select v-model.number="filtros.limite" class="filtro-select">
          <option :value="25">25 registros</option>
          <option :value="50">50 registros</option>
          <option :value="100">100 registros</option>
        </select>
      </div>
      <button class="btn-buscar" @click="cargar">
        <i class="bi bi-search"></i> Buscar
      </button>
    </div>

    <!-- Tabla -->
    <div class="table-wrap">
      <div v-if="registros.length === 0 && !isLoading && !intentado" class="empty-msg">
        <i class="bi bi-shield-check" style="font-size:2.2rem"></i>
        <p>Cargando actividad...</p>
      </div>

      <div v-else-if="registros.length === 0 && !isLoading" class="empty-msg">
        <i class="bi bi-inbox" style="font-size:2.2rem"></i>
        <p>No hay actividad registrada para los filtros seleccionados.</p>
      </div>

      <table v-else class="audit-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Fecha y hora</th>
            <th>Usuario</th>
            <th>Módulo</th>
            <th>Acción</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(r, i) in registros" :key="r.id_auditoria">
            <tr :class="'row-' + r.accion.toLowerCase()">
              <td class="num-col">{{ offset + i + 1 }}</td>
              <td class="fecha-col">{{ formatFecha(r.fecha) }}</td>
              <td class="usuario-col">
                <span class="usuario-nombre">{{ r.usuario }}</span>
              </td>
              <td>
                <span class="tabla-badge">
                  <i :class="iconoModulo(r.tabla)" style="margin-right:4px"></i>
                  {{ etiquetaTabla(r.tabla) }}
                </span>
              </td>
              <td>
                <span class="accion-badge" :class="'accion-' + r.accion.toLowerCase()">
                  <i :class="iconoAccion(r.accion)" style="margin-right:3px"></i>
                  {{ etiquetaAccion(r.accion) }}
                </span>
              </td>
              <td>
                <button class="btn-ver" @click="toggleDetalle(r.id_auditoria)" :title="detalleAbierto === r.id_auditoria ? 'Ocultar detalle' : 'Ver detalle'">
                  <i :class="detalleAbierto === r.id_auditoria ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                  {{ detalleAbierto === r.id_auditoria ? 'Ocultar' : 'Ver cambios' }}
                </button>
              </td>
            </tr>
            <tr v-if="detalleAbierto === r.id_auditoria" class="detalle-row">
              <td colspan="6">
                <div class="detalle-contenido">
                  <!-- INSERT: mostrar nuevos datos -->
                  <div v-if="r.accion === 'INSERT' && r.datos_despues" class="detalle-seccion">
                    <div class="detalle-seccion-title insert-title">
                      <i class="bi bi-plus-circle-fill"></i> Datos del nuevo registro
                    </div>
                    <div class="campos-grid">
                      <div v-for="[key, val] in camposLegibles(r.datos_despues)" :key="key" class="campo-item">
                        <span class="campo-clave">{{ etiquetaCampo(key) }}</span>
                        <span class="campo-valor">{{ formatValor(val) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- DELETE: mostrar datos eliminados -->
                  <div v-else-if="r.accion === 'DELETE' && r.datos_antes" class="detalle-seccion">
                    <div class="detalle-seccion-title delete-title">
                      <i class="bi bi-trash-fill"></i> Registro eliminado
                    </div>
                    <div class="campos-grid">
                      <div v-for="[key, val] in camposLegibles(r.datos_antes)" :key="key" class="campo-item">
                        <span class="campo-clave">{{ etiquetaCampo(key) }}</span>
                        <span class="campo-valor eliminado">{{ formatValor(val) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- UPDATE: mostrar solo campos que cambiaron -->
                  <div v-else-if="r.accion === 'UPDATE'" class="detalle-seccion">
                    <div class="detalle-seccion-title update-title">
                      <i class="bi bi-pencil-fill"></i> Campos modificados
                    </div>
                    <div v-if="camposModificados(r.datos_antes, r.datos_despues).length === 0" class="sin-cambios">
                      Sin cambios detectados en los datos.
                    </div>
                    <div v-else class="cambios-lista">
                      <div v-for="cambio in camposModificados(r.datos_antes, r.datos_despues)" :key="cambio.campo" class="cambio-item">
                        <span class="cambio-campo">{{ etiquetaCampo(cambio.campo) }}</span>
                        <span class="cambio-antes">{{ formatValor(cambio.antes) }}</span>
                        <i class="bi bi-arrow-right cambio-flecha"></i>
                        <span class="cambio-despues">{{ formatValor(cambio.despues) }}</span>
                      </div>
                    </div>
                  </div>

                  <div v-else class="sin-cambios">Sin detalle disponible para este registro.</div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div v-if="total > 0" class="pagination-wrapper">
      <div class="pagination-info">
        Mostrando {{ offset + 1 }} a {{ Math.min(offset + filtros.limite, total) }} de {{ total }} registros
      </div>
      <div class="pagination-container">
        <button class="pagination-button" :disabled="offset === 0" @click="paginaAnterior">Anterior</button>
        <button class="pagination-button" :disabled="offset + filtros.limite >= total" @click="paginaSiguiente">Siguiente</button>
      </div>
    </div>
  </div>
</template>

<script>
import ModalLoading from "@/components/ModalLoading.vue";
import solicitudes from "../../services/solicitudes.js";
import { notis } from "../../services/notificaciones.js";
import { setPageTitle } from "@/components/pageMetadata";

const TABLA_ETIQUETAS = {
  Ventas:              { label: 'Ventas',         icon: 'bi bi-receipt' },
  Clientes:            { label: 'Clientes',        icon: 'bi bi-people' },
  Productos:           { label: 'Inventario',      icon: 'bi bi-box-seam' },
  Proveedores:         { label: 'Proveedores',     icon: 'bi bi-truck' },
  Empleados:           { label: 'Empleados',       icon: 'bi bi-person-badge' },
  Usuarios:            { label: 'Usuarios',        icon: 'bi bi-person-gear' },
  caja:                { label: 'Caja',            icon: 'bi bi-cash-stack' },
  facturas:            { label: 'Facturas',        icon: 'bi bi-file-earmark-text' },
  detalle_venta:       { label: 'Detalle de venta',icon: 'bi bi-list-ul' },
  pagos_credito:       { label: 'Pagos a crédito', icon: 'bi bi-credit-card' },
  cuentas_por_cobrar:  { label: 'Créditos',        icon: 'bi bi-wallet2' },
  cuentas_por_pagar:   { label: 'Cuentas por pagar',icon:'bi bi-bank' },
  Categorias:          { label: 'Categorías',      icon: 'bi bi-tag' },
  sucursales:          { label: 'Sucursales',      icon: 'bi bi-building' },
  alerts:              { label: 'Alertas',         icon: 'bi bi-bell' },
  auditoria:           { label: 'Auditoría',       icon: 'bi bi-shield-check' },
};

const CAMPO_ETIQUETAS = {
  id_venta: 'ID Venta', id_cliente: 'Cliente', id_usuario: 'Usuario',
  id_producto: 'Producto', id_caja: 'Caja', id_sucursal: 'Sucursal',
  estado: 'Estado', total: 'Total', sub_total: 'Subtotal',
  total_ISV: 'ISV', descuento: 'Descuento', tipo_factura: 'Método de pago',
  nombre: 'Nombre', apellido: 'Apellido', correo: 'Correo',
  telefono: 'Teléfono', direccion: 'Dirección', rtn: 'RTN',
  tipo_cliente: 'Tipo de cliente', descuento_habitual: 'Descuento habitual',
  nombre_producto: 'Producto', precio_venta: 'Precio de venta',
  precio_compra: 'Precio de compra', stock: 'Stock',
  stock_minimo: 'Stock mínimo', codigo_producto: 'Código',
  codigo_factura: 'N° Factura', monto_pago: 'Monto pagado',
  metodo_pago: 'Método de pago', monto_efectivo: 'Efectivo',
  monto_transferencia: 'Transferencia', numero_transferencia: 'N° Transferencia',
  saldo_pendiente: 'Saldo pendiente', notas: 'Notas',
  abierto: 'Estado caja', monto_inicial: 'Monto inicial',
  monto_final: 'Monto final', diferencia: 'Diferencia',
  fecha_apertura: 'Fecha apertura', fecha_cierre: 'Fecha cierre',
  created_at: 'Fecha de creación', updated_at: 'Última modificación',
  activo: 'Activo', descripcion: 'Descripción',
  nombre_completo: 'Nombre completo', fecha_nacimiento: 'Fecha de nacimiento',
};

const CAMPOS_OCULTOS = new Set([
  'id_auditoria', 'id_empresa', 'id_sucursal', 'id_factura', 'id_detalle',
  'id_pago', 'id_cuenta', 'id_proveedor', 'id_empleado', 'id_categoria',
  'id_alerta', 'ip_address', 'hash', 'token', 'lote',
]);

// Orden lógico de campos — los que no están aquí van al final, alfabéticamente
const CAMPOS_ORDEN = [
  'nombre', 'nombre_usuario', 'codigo', 'tipo', 'estado', 'activo',
  'descripcion', 'notas',
  'precio_unitario', 'precio_mayorista', 'costo_promedio',
  'impuesto', 'descuento',
  'stock_actual', 'stock_minimo',
  'id_producto', 'producto',
  'unidad_compra', 'unidad_produccion', 'id_unidad_medida', 'factor_conversion',
  'cantidad_activar_mayorista',
  'monto', 'total', 'subtotal',
  'correo', 'telefono', 'direccion',
  'created_at', 'fecha_creacion', 'fecha_vencimiento', 'fecha_pago',
];

export default {
  name: 'AuditoriaPage',
  components: { ModalLoading },

  data() {
    return {
      isLoading: false,
      intentado: false,
      id_usuario: null,
      registros: [],
      total: 0,
      offset: 0,
      detalleAbierto: null,
      tablasDisponibles: [],
      filtros: {
        tabla: '',
        accion: '',
        fecha_inicio: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        fecha_fin: new Date().toISOString().slice(0, 10),
        limite: 50,
      },
    };
  },

  async mounted() {
    setPageTitle('Actividad del sistema');
    this.id_usuario = await solicitudes.solicitarUsuarioToken();
    this.cargarTablas();
    this.cargar();
  },

  methods: {
    cargarTablas() {
      this.tablasDisponibles = Object.entries(TABLA_ETIQUETAS).map(([valor, meta]) => ({
        valor,
        etiqueta: meta.label,
      }));
    },

    async cargar() {
      this.isLoading = true;
      this.intentado = true;
      this.offset = 0;
      await this.fetchRegistros();
    },

    async fetchRegistros() {
      this.isLoading = true;
      try {
        const token = localStorage.getItem('auth');
        const { getApi } = await import('../../config/getApiUrl.js');
        const params = new URLSearchParams({
          limite: this.filtros.limite,
          offset: this.offset,
          ...(this.filtros.tabla        && { tabla:        this.filtros.tabla }),
          ...(this.filtros.accion       && { accion:       this.filtros.accion }),
          ...(this.filtros.fecha_inicio && { fecha_inicio: this.filtros.fecha_inicio }),
          ...(this.filtros.fecha_fin    && { fecha_fin:    this.filtros.fecha_fin }),
        });
        const res = await fetch(`${getApi()}/auditoria?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        this.registros = data.registros || [];
        this.total = data.total || 0;
      } catch (e) {
        notis('error', 'Error al cargar actividad: ' + e.message);
      } finally {
        this.isLoading = false;
      }
    },

    paginaAnterior() {
      this.offset = Math.max(0, this.offset - this.filtros.limite);
      this.fetchRegistros();
    },

    paginaSiguiente() {
      this.offset += this.filtros.limite;
      this.fetchRegistros();
    },

    toggleDetalle(id) {
      this.detalleAbierto = this.detalleAbierto === id ? null : id;
    },

    etiquetaTabla(tabla) {
      return TABLA_ETIQUETAS[tabla]?.label || tabla;
    },

    iconoModulo(tabla) {
      return TABLA_ETIQUETAS[tabla]?.icon || 'bi bi-grid';
    },

    etiquetaAccion(accion) {
      const mapa = { INSERT: 'Agregado', UPDATE: 'Modificado', DELETE: 'Eliminado' };
      return mapa[accion] || accion;
    },

    iconoAccion(accion) {
      const mapa = { INSERT: 'bi bi-plus-circle', UPDATE: 'bi bi-pencil', DELETE: 'bi bi-trash' };
      return mapa[accion] || 'bi bi-circle';
    },

    etiquetaCampo(campo) {
      return CAMPO_ETIQUETAS[campo] || campo.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    camposLegibles(datos) {
      if (!datos || typeof datos !== 'object') return [];
      const entries = Object.entries(datos).filter(([k]) => !CAMPOS_OCULTOS.has(k));
      const prioIdx = k => {
        const i = CAMPOS_ORDEN.indexOf(k);
        return i === -1 ? CAMPOS_ORDEN.length : i;
      };
      entries.sort(([a], [b]) => {
        const diff = prioIdx(a) - prioIdx(b);
        return diff !== 0 ? diff : a.localeCompare(b);
      });
      return entries; // [[key, val], ...]
    },

    camposModificados(antes, despues) {
      if (!antes || !despues) return [];
      const cambios = [];
      const claves = new Set([...Object.keys(antes), ...Object.keys(despues)]);
      for (const k of claves) {
        if (CAMPOS_OCULTOS.has(k)) continue;
        const a = antes[k], d = despues[k];
        if (JSON.stringify(a) !== JSON.stringify(d)) {
          cambios.push({ campo: k, antes: a, despues: d });
        }
      }
      return cambios;
    },

    formatValor(val) {
      if (val === null || val === undefined) return '—';
      if (typeof val === 'boolean') return val ? 'Sí' : 'No';
      if (typeof val === 'object') return JSON.stringify(val);
      // Detectar fechas ISO
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
        return new Date(val).toLocaleString('es-HN', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        });
      }
      return String(val);
    },

    formatFecha(f) {
      if (!f) return '—';
      return new Date(f).toLocaleString('es-HN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; font-family: 'Montserrat', sans-serif; }

.auditoria-wrapper {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.auditoria-header { margin-bottom: 20px; }

.auditoria-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
}

.auditoria-sub {
  margin: 0;
  font-size: 0.87rem;
  color: #6b7280;
}

.dark .auditoria-title { color: #f3f4f6; }
.dark .auditoria-sub   { color: #9ca3af; }

/* Filtros */
.filtros-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.08);
  margin-bottom: 20px;
}

.dark .filtros-bar { background: #2d2d2d; }

.filtro-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 150px;
}

.filtro-group label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.dark .filtro-group label { color: #9ca3af; }

.filtro-select, .filtro-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.87rem;
  background: #f9fafb;
  color: #1f2937;
}

.dark .filtro-select, .dark .filtro-input {
  background: #383838;
  border-color: #555;
  color: #e5e7eb;
}

.btn-buscar {
  padding: 7px 18px;
  background: #c09d62;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.87rem;
  font-weight: 600;
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background .15s;
}
.btn-buscar:hover { background: #a38655; }

/* Table */
.table-wrap {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.08);
  overflow-x: auto;
  margin-bottom: 16px;
}

.dark .table-wrap { background: #2d2d2d; }

.empty-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px;
  color: #9ca3af;
}
.empty-msg p { margin: 0; font-size: 0.9rem; }

.audit-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.87rem;
}

.audit-table th {
  padding: 10px 12px;
  background: #f3f4f6;
  color: #374151;
  font-weight: 700;
  text-align: left;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
}

.dark .audit-table th { background: #383838; color: #e5e7eb; border-color: #4b5563; }

.audit-table td {
  padding: 9px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
  vertical-align: middle;
}

.dark .audit-table td { border-color: #374151; color: #e5e7eb; }

.audit-table tbody tr:hover > td { background: #f9fafb; }
.dark .audit-table tbody tr:hover > td { background: #383838; }

.num-col { text-align: center; font-variant-numeric: tabular-nums; white-space: nowrap; }
.fecha-col { white-space: nowrap; color: #6b7280; font-size: 0.82rem; }
.dark .fecha-col { color: #9ca3af; }
.usuario-col { white-space: nowrap; }
.usuario-nombre { font-weight: 600; }

/* Row coloring by action */
.row-insert td { border-left: 3px solid #16a34a; }
.row-update td { border-left: 3px solid #2563eb; }
.row-delete td { border-left: 3px solid #dc2626; }

/* Badges */
.tabla-badge {
  background: #e0e7ff;
  color: #3730a3;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.dark .tabla-badge { background: rgba(99,102,241,0.2); color: #a5b4fc; }

.accion-badge {
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.accion-insert { background: #d1fae5; color: #065f46; }
.accion-update { background: #dbeafe; color: #1d4ed8; }
.accion-delete { background: #fee2e2; color: #991b1b; }

.dark .accion-insert { background: rgba(16,185,129,0.2); color: #6ee7b7; }
.dark .accion-update { background: rgba(37,99,235,0.2);  color: #93c5fd; }
.dark .accion-delete { background: rgba(220,38,38,0.2);  color: #fca5a5; }

/* Botón ver detalle */
.btn-ver {
  background: #0e7490;
  color: #fff !important;
  border: 1px solid #0e7490;
  border-radius: 4px;
  padding: 3px 10px;
  cursor: pointer;
  color: #6b7280;
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all .15s;
  white-space: nowrap;
}
.btn-ver:hover { background: #0a5d73; border-color: #0a5d73; }
.dark .btn-ver { background: #0e7490; border-color: #0e7490; color: #fff; }
.dark .btn-ver:hover { background: #0a5d73; border-color: #0a5d73; }

/* Detalle expandido */
.detalle-row td { background: #f8fafc !important; padding: 14px 16px; color: #1f2937; }
.dark .detalle-row td { background: #252525 !important; color: #e5e7eb; }

.detalle-contenido {
  max-width: 900px;
  background: transparent;
}

.detalle-seccion { /* container */ }

.detalle-seccion-title {
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 10px;
  border-radius: 4px;
  margin-bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.insert-title { background: #d1fae5; color: #065f46; }
.update-title { background: #dbeafe; color: #1d4ed8; }
.delete-title { background: #fee2e2; color: #991b1b; }

.dark .insert-title { background: rgba(16,185,129,0.2); color: #6ee7b7; }
.dark .update-title { background: rgba(37,99,235,0.2);  color: #93c5fd; }
.dark .delete-title { background: rgba(220,38,38,0.2);  color: #fca5a5; }

/* Campos grid — INSERT/DELETE */
.campos-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  background: transparent;
}

.campo-item {
  display: flex;
  flex-direction: column;
  min-width: 160px;
  max-width: 260px;
  background: transparent;
}

.campo-clave {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
  margin-bottom: 2px;
}
.dark .campo-clave { color: #9ca3af; }

.campo-valor {
  font-size: 0.87rem;
  color: #1f2937;
  word-break: break-word;
}
.dark .campo-valor { color: #e5e7eb; }

.campo-valor.eliminado {
  text-decoration: line-through;
  color: #991b1b;
}
.dark .campo-valor.eliminado { color: #fca5a5; }

/* Cambios — UPDATE */
.cambios-lista {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cambio-item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
}
.dark .cambio-item { background: #2d2d2d; border-color: #4b5563; }

.cambio-campo {
  font-size: 0.78rem;
  font-weight: 700;
  color: #6b7280;
  min-width: 140px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.dark .cambio-campo { color: #9ca3af; }

.cambio-antes {
  font-size: 0.87rem;
  color: #991b1b;
  text-decoration: line-through;
  background: #fee2e2;
  padding: 2px 6px;
  border-radius: 4px;
}
.dark .cambio-antes { background: rgba(220,38,38,0.15); color: #fca5a5; }

.cambio-flecha {
  color: #9ca3af;
  font-size: 0.85rem;
}

.cambio-despues {
  font-size: 0.87rem;
  color: #065f46;
  background: #d1fae5;
  padding: 2px 6px;
  border-radius: 4px;
}
.dark .cambio-despues { background: rgba(16,185,129,0.15); color: #6ee7b7; }

.sin-cambios {
  font-size: 0.87rem;
  color: #9ca3af;
  font-style: italic;
}

/* Paginación */
.pagination-wrapper { display: flex; justify-content: space-between; align-items: center; padding: 1rem; border-top: 1px solid #dee2e6; }
.pagination-info { color: #6c757d; font-size: 0.875rem; }
.pagination-container { display: flex; gap: 5px; }
.pagination-button { padding: 6px 14px; border: 1px solid #dee2e6; background: white; cursor: pointer; border-radius: 6px; font-size: 0.875rem; font-family: "Montserrat", sans-serif; transition: all 0.3s ease; color: #212529; }
.pagination-button:hover:not(:disabled) { background: #c09d62; color: white; border-color: #c09d62; }
.pagination-button:disabled { opacity: 0.5; cursor: not-allowed; }
.dark .pagination-wrapper { border-color: #404040; }
.dark .pagination-info { color: #9e9e9e; }
.dark .pagination-button { background: #2d2d2d; color: #e5e5e5; border-color: #404040; }
.dark .pagination-button:hover:not(:disabled) { background: #c09d62; color: white; border-color: #c09d62; }
</style>
